/**
 * Pricing and subscription status. Pure functions — no database, no clock.
 * Both the front desk and the portal import these, so "expired" and
 * "outstanding" mean exactly the same number in both places.
 *
 * IMPORTANT: prices are TOTALS FOR THE WHOLE TERM, not per month.
 * The schedule has bulk discounts baked in — an adult club member pays 2,250
 * for one month but 6,000 for three, not 6,750. There is deliberately no
 * "price per month" anywhere in the system, because multiplying it would
 * overcharge every multi-month sale.
 */
import type {
  ISODate,
  Payment,
  Subscription,
  SubscriptionStatus,
} from './types';
import { daysRemaining } from './dates';
import { sessionPricePiastres } from './cancellations';
import { sumPiastres } from './money';

/** A subscription is "expiring soon" inside this many days. */
export const EXPIRING_SOON_DAYS = 7;

/* -------------------------------------------------------------------------
   The price schedule
   ------------------------------------------------------------------------- */

/** The academy sells exactly these three terms. */
export const TERM_MONTHS = [1, 3, 6] as const;
export type TermMonths = (typeof TERM_MONTHS)[number];

export type AgeGroup = 'adults' | 'kids';
/** Group training, or one-to-one. */
export type TrainingMode = 'group' | 'private';
/** Club house members pay a reduced rate. */
export type MembershipType = 'club_member' | 'non_member';

export const MEMBERSHIP_LABELS: Record<MembershipType, string> = {
  club_member: 'Club house member',
  non_member: 'Non-member',
};

export const TRAINING_MODE_LABELS: Record<TrainingMode, string> = {
  group: 'Group',
  private: 'Private',
};

/**
 * A stable key for one cell of the price schedule, e.g.
 * `adults_private_club_member_3m`.
 */
export function priceKey(
  ageGroup: AgeGroup,
  mode: TrainingMode,
  membership: MembershipType,
  months: TermMonths
): string {
  return `${ageGroup}_${mode}_${membership}_${months}m`;
}

/**
 * City View Branch — Club House Subscriptions Price Schedule.
 * Values in piastres (1 EGP = 100). Editable in Settings; this is only the seed.
 *
 *                  MEMBER              NON-MEMBER
 *                  1m     3m     6m    1m     3m     6m
 *   Adult          2,250  6,000  11,000  2,500  6,500  12,000
 *   Kids           1,500  4,000   7,000  1,700  4,500   8,000
 *   Adult Private  3,600 10,000  19,000  4,000 11,000  20,000
 *   Kids Private   2,600  7,000  13,000  3,000  8,000  14,500
 */
const SCHEDULE_EGP: Record<string, [number, number, number]> = {
  // [1 month, 3 months, 6 months]
  adults_group_club_member: [2_250, 6_000, 11_000],
  adults_group_non_member: [2_500, 6_500, 12_000],
  kids_group_club_member: [1_500, 4_000, 7_000],
  kids_group_non_member: [1_700, 4_500, 8_000],
  adults_private_club_member: [3_600, 10_000, 19_000],
  adults_private_non_member: [4_000, 11_000, 20_000],
  kids_private_club_member: [2_600, 7_000, 13_000],
  kids_private_non_member: [3_000, 8_000, 14_500],
};

export interface PriceScheduleEntry {
  key: string;
  ageGroup: AgeGroup;
  mode: TrainingMode;
  membership: MembershipType;
  months: TermMonths;
  pricePiastres: number;
}

/** The full 24-cell schedule, for seeding and for the Settings editor. */
export function defaultPriceSchedule(): PriceScheduleEntry[] {
  const entries: PriceScheduleEntry[] = [];

  for (const ageGroup of ['adults', 'kids'] as const) {
    for (const mode of ['group', 'private'] as const) {
      for (const membership of ['club_member', 'non_member'] as const) {
        const row = SCHEDULE_EGP[`${ageGroup}_${mode}_${membership}`];
        if (!row) continue;
        TERM_MONTHS.forEach((months, i) => {
          entries.push({
            key: priceKey(ageGroup, mode, membership, months),
            ageGroup,
            mode,
            membership,
            months,
            pricePiastres: (row[i] ?? 0) * 100,
          });
        });
      }
    }
  }

  return entries;
}

const DEFAULTS_BY_KEY = new Map(defaultPriceSchedule().map((e) => [e.key, e.pricePiastres]));

/** The seeded price for one cell, before any edit the owner has made. */
export function defaultPriceFor(
  ageGroup: AgeGroup,
  mode: TrainingMode,
  membership: MembershipType,
  months: TermMonths
): number {
  return DEFAULTS_BY_KEY.get(priceKey(ageGroup, mode, membership, months)) ?? 0;
}

/** Human label for a schedule row, e.g. "Adult Private · Non-member · 3 months". */
export function describePriceKey(entry: PriceScheduleEntry): string {
  const tier =
    entry.ageGroup === 'adults'
      ? entry.mode === 'private'
        ? 'Adult Private'
        : 'Adult'
      : entry.mode === 'private'
        ? 'Kids Private'
        : 'Kids';
  return `${tier} · ${MEMBERSHIP_LABELS[entry.membership]} · ${entry.months} month${
    entry.months === 1 ? '' : 's'
  }`;
}

/** A walk-in (single drop-in session) is 300 EGP for any class. */
export const DEFAULT_WALK_IN_PRICE_PIASTRES = 30_000;

/* -------------------------------------------------------------------------
   Totals
   ------------------------------------------------------------------------- */

/**
 * What the member owes.
 *
 *   total_due = scheduled price for the term − discount
 *
 * Clamped at zero: a discount larger than the price is a data-entry error, and
 * a negative total would read as the gym owing the member money.
 */
export function computeTotalDue(pricePiastres: number, discountPiastres: number): number {
  return Math.max(0, Math.round(pricePiastres) - Math.round(discountPiastres));
}

/** What has actually been collected against a subscription. */
export function computeAmountPaid(payments: readonly Payment[]): number {
  return sumPiastres(
    payments.filter((p) => p.deleted_at === null).map((p) => p.amount_piastres)
  );
}

/**
 * What is still owed. Clamped at zero so an overpayment shows as settled
 * rather than as a negative balance the front desk has to explain.
 */
export function computeRemaining(totalDuePiastres: number, amountPaidPiastres: number): number {
  return Math.max(0, totalDuePiastres - amountPaidPiastres);
}

/** True when the amber "Balance due" badge should appear. */
export function hasBalanceDue(totalDuePiastres: number, amountPaidPiastres: number): boolean {
  return computeRemaining(totalDuePiastres, amountPaidPiastres) > 0;
}

/**
 * What one subscription still owes — the figure every balance in the system
 * sums, and the ONLY one that is correct for a cancelled subscription.
 *
 * A live subscription owes its term total less what has been paid. A CANCELLED
 * one does not: the member gave back the unused part of the bundle and owes
 * only the sessions they actually used.
 *
 * This matters because a refund is a negative payment. Measuring a cancelled
 * subscription against the full term total counts that negative row as an
 * unpaid balance, so a member who was refunded in full — the commonest
 * cancellation there is — reads as owing exactly the amount they were handed
 * back. Every screen that lists debts would then chase them for it.
 *
 * `sessions_attended` and the term total are both snapshotted on the row, so
 * what was consumed is recomputable and no new column is needed.
 */
export function computeSubscriptionRemaining(
  subscription: Pick<
    Subscription,
    'cancelled' | 'sessions_attended' | 'total_due_piastres' | 'months'
  >,
  amountPaidPiastres: number
): number {
  if (!subscription.cancelled) {
    return computeRemaining(subscription.total_due_piastres, amountPaidPiastres);
  }

  // Cancelled before this feature existed, so there is no session count to
  // price. Nothing is owed either way — the term is over.
  if (subscription.sessions_attended === null) return 0;

  const consumed =
    sessionPricePiastres(subscription.total_due_piastres, subscription.months) *
    Math.max(0, Math.trunc(subscription.sessions_attended));

  return computeRemaining(consumed, amountPaidPiastres);
}

/**
 * The effective monthly rate, for display only. Never used to compute a total —
 * the schedule price is the truth.
 */
export function effectiveMonthlyRate(pricePiastres: number, months: number): number {
  return months > 0 ? Math.round(pricePiastres / months) : 0;
}

/* -------------------------------------------------------------------------
   Status
   ------------------------------------------------------------------------- */

/**
 * Status is DERIVED, never stored — storing it would mean a subscription only
 * expires if some background job remembers to run. Here, a membership expires
 * because the date passed, full stop.
 */
export function deriveStatus(
  sub: Subscription,
  today: ISODate,
  /** True when a live freeze covers `today`. Ranks above the date maths. */
  isFrozen = false
): SubscriptionStatus {
  if (sub.cancelled) return 'cancelled';
  // A frozen member is not expiring — their end date was already pushed out,
  // so showing "expiring soon" here would send the front desk chasing someone
  // who is on holiday.
  if (isFrozen) return 'frozen';
  const remaining = daysRemaining(sub.end_date, today);
  if (remaining < 0) return 'expired';
  if (remaining <= EXPIRING_SOON_DAYS) return 'expiring_soon';
  return 'active';
}

export const STATUS_LABELS: Record<SubscriptionStatus, string> = {
  active: 'Active',
  expiring_soon: 'Expiring soon',
  expired: 'Expired',
  cancelled: 'Cancelled',
  frozen: 'Frozen',
};

export const STATUS_CLASSES: Record<SubscriptionStatus, string> = {
  active: 'border-status-active/40 bg-status-activeBg text-status-active',
  expiring_soon: 'border-status-expiring/40 bg-status-expiringBg text-status-expiring',
  expired: 'border-status-expired/40 bg-status-expiredBg text-status-expired',
  cancelled: 'border-status-cancelled/40 bg-status-cancelledBg text-status-cancelled',
  frozen: 'border-status-frozen/40 bg-status-frozenBg text-status-frozen',
};

/** Plain-language countdown, so staff never subtract dates in their head. */
export function describeRemaining(days: number): string {
  if (days === 0) return 'Last day';
  if (days === 1) return '1 day left';
  if (days > 1) return `${days} days left`;
  const ago = Math.abs(days);
  return ago === 1 ? 'Expired yesterday' : `Expired ${ago} days ago`;
}
